<template>
  <div ref="chart">
    <Chart :height="height" :id="id" :option="option" :width="width"></Chart>
  </div>
</template>

<script>
import Chart from '@/components/echarts/Chart';

export default {
  name: "HumitureDeviceBarChart",
  components: { Chart },
  props: {
    data: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      id: "HumitureDeviceBarChart_" + Math.random().toString(36).substr(2, 9),
      width: '100%',
      height: '100%',
      option: {}
    };
  },
  watch: {
    data: {
      handler() {
        this.getOption();
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.getOption();
  },
  methods: {
    getOption() {
      if (!this.data) return;
      
      const { deviceNames, statuses, temperatures, humidities, thresholds, tempName, humiName } = this.data;
      
      const tName = tempName || '温度';
      const hName = humiName || '湿度';

      // 构建温度阈值线
      const tempMarkLineData = [];
      if (thresholds && thresholds.temperature) {
        const tempList = Array.isArray(thresholds.temperature) ? thresholds.temperature : [thresholds.temperature];
        tempList.forEach(t => {
          const namePrefix = t.pointName || tName;
          const level = t.levelName || '';
          if (t.alarmHigh != null) {
            tempMarkLineData.push({
              yAxis: t.alarmHigh,
              name: `${namePrefix}${level}上限`,
              lineStyle: { color: t.levelColor || '#ff0000', type: 'dashed' },
              label: { 
                show: true, 
                position: 'insideEndTop',
                formatter: `${namePrefix}${level}上限: {c}℃`,
                color: t.levelColor || '#ff0000',
                fontSize: 10
              }
            });
          }
          if (t.alarmLow != null) {
            tempMarkLineData.push({
              yAxis: t.alarmLow,
              name: `${namePrefix}${level}下限`,
              lineStyle: { color: t.levelColor || '#ff0000', type: 'dashed' },
              label: { 
                show: true, 
                position: 'insideEndBottom',
                formatter: `${namePrefix}${level}下限: {c}℃`,
                color: t.levelColor || '#ff0000',
                fontSize: 10
              }
            });
          }
        });
      }
      
      // 构建湿度阈值线
      const humiMarkLineData = [];
      if (thresholds && thresholds.humidity) {
        const humiList = Array.isArray(thresholds.humidity) ? thresholds.humidity : [thresholds.humidity];
        humiList.forEach(h => {
          const namePrefix = h.pointName || hName;
          const level = h.levelName || '';
          if (h.alarmHigh != null) {
            humiMarkLineData.push({
              yAxis: h.alarmHigh,
              name: `${namePrefix}${level}上限`,
              lineStyle: { color: h.levelColor || '#ff0000', type: 'dashed' },
              label: { 
                show: true, 
                position: 'insideStartTop',
                formatter: `${namePrefix}${level}上限: {c}%`,
                color: h.levelColor || '#ff0000',
                fontSize: 10
              }
            });
          }
          if (h.alarmLow != null) {
            humiMarkLineData.push({
              yAxis: h.alarmLow,
              name: `${namePrefix}${level}下限`,
              lineStyle: { color: h.levelColor || '#ff0000', type: 'dashed' },
              label: { 
                show: true, 
                position: 'insideStartBottom',
                formatter: `${namePrefix}${level}下限: {c}%`,
                color: h.levelColor || '#ff0000',
                fontSize: 10
              }
            });
          }
        });
      }
      
      this.option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            let result = params[0].name + '<br/>';
            params.forEach(item => {
              if (item.componentType === 'series') {
                const unit = item.seriesName === tName ? '℃' : '%';
                result += item.marker + item.seriesName + ': ' + item.value + unit + '<br/>';
              }
            });
            return result;
          }
        },
        legend: {
          data: [tName, hName],
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          top: 10,
          left: 'center'
        },
        grid: {
          left: '5%',
          right: '5%',
          bottom: '10%',
          top: '20%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: deviceNames,
          axisLine: {
            lineStyle: {
              color: 'rgba(73, 164, 243, .4)'
            }
          },
          axisLabel: {
            color: (value, index) => {
              const status = statuses && statuses[index];
              // 0:通讯正常，1:通讯异常，-1:未连接，2:有告警
              if (status !== undefined && status !== 0) {
                return '#ff0000';
              }
              return '#fff';
            },
            fontSize: 11,
            interval: 0,
            rotate: 0,
            formatter: function(value) {
              if (value.length > 6) {
                return value.substring(0, 6) + '...';
              }
              return value;
            }
          },
          axisTick: {
            show: false
          }
        },
        yAxis: [
          {
            type: 'value',
            name: `${tName}(℃)`,
            nameTextStyle: {
              color: '#299BFF',
              fontSize: 11
            },
            position: 'left',
            axisLine: {
              show: true,
              lineStyle: {
                color: 'rgba(73, 164, 243, .4)'
              }
            },
            axisLabel: {
              color: '#299BFF',
              fontSize: 11
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: 'rgba(73, 164, 243, .2)'
              }
            }
          },
          {
            type: 'value',
            name: `${hName}(%)`,
            nameTextStyle: {
              color: '#32CFD2',
              fontSize: 11
            },
            position: 'right',
            axisLine: {
              show: true,
              lineStyle: {
                color: 'rgba(73, 164, 243, .4)'
              }
            },
            axisLabel: {
              color: '#32CFD2',
              fontSize: 11
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: tName,
            type: 'bar',
            barWidth: '20%',
            barGap: '90%',
            yAxisIndex: 0,
            data: temperatures,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(41, 155, 255, 1)' },
                  { offset: 0.5, color: 'rgba(41, 155, 255, 0.7)' },
                  { offset: 1, color: 'rgba(41, 155, 255, 0.3)' }
                ]
              },
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: true,
              position: 'top',
              color: '#299BFF',
              fontSize: 9,
              formatter: '{c}℃'
            },
            markLine: tempMarkLineData.length > 0 ? {
              silent: true,
              symbol: 'none',
              data: tempMarkLineData
            } : null
          },
          {
            name: hName,
            type: 'bar',
            barWidth: '20%',
            yAxisIndex: 1,
            data: humidities,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(50, 207, 210, 1)' },
                  { offset: 0.5, color: 'rgba(50, 207, 210, 0.7)' },
                  { offset: 1, color: 'rgba(50, 207, 210, 0.3)' }
                ]
              },
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: true,
              position: 'top',
              color: '#32CFD2',
              fontSize: 9,
              formatter: '{c}%'
            },
            markLine: humiMarkLineData.length > 0 ? {
              silent: true,
              symbol: 'none',
              data: humiMarkLineData
            } : null
          }
        ]
      };
    }
  }
}
</script>

<style scoped>
</style>
